import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { 
  FaHeart, 
  FaTrash, 
  FaSearch, 
  FaFilter, 
  FaSort, 
  FaVolumeUp, 
  FaStar, 
  FaStarHalfAlt,
  FaLeaf,
  FaSeedling,
  FaTree,
  FaArrowLeft,
  FaPlus,
  FaBookmark,
  FaChartLine,
  FaClock,
  FaCheckCircle,
  FaCog
} from 'react-icons/fa';
import { useAuth } from '../contexts/AuthContext';
import { playSmartAudio } from '../utils/audioUtils';
import { env } from '../config/environment';

interface GardenWord {
  wordId: string;
  farsi: string;
  english: string;
  transliteration: string;
  category?: string;
  difficulty?: string;
  addedAt: string;
  lastReviewed?: string;
  reviewCount: number;
  mastery: number;
  notes?: string;
}

interface GardenStats {
  totalWords: number;
  categories: string[];
  lastUpdated: string;
  wordsNeedingReview: number;
  averageMastery: number;
  difficultyBreakdown: {
    beginner: number;
    intermediate: number;
    advanced: number;
    expert: number;
  };
}

const MyGarden: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [gardenWords, setGardenWords] = useState<GardenWord[]>([]);
  const [stats, setStats] = useState<GardenStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [selectedDifficulty, setSelectedDifficulty] = useState<string>('all');
  const [sortBy, setSortBy] = useState<'addedAt' | 'mastery' | 'farsi' | 'english'>('addedAt');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  // Removed local API switching - always use production API

  useEffect(() => {
    if (!user) {
      console.log('No user found, redirecting to login');
      navigate('/login');
      return;
    }
    console.log('User found, fetching garden:', user);
    fetchGarden();
  }, [user, navigate]);

  const fetchGarden = async () => {
    try {
      setLoading(true);
      setError(null);

      const token = localStorage.getItem('authToken');
      if (!token) {
        console.log('No auth token found, redirecting to login');
        navigate('/login');
        return;
      }

      // Always use production API URL
      const apiUrl = `${env.apiUrl}/api/garden`;

      console.log('Fetching garden from:', apiUrl);
      console.log('Using token:', token.substring(0, 20) + '...');
      
      const response = await fetch(apiUrl, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      console.log('Garden response status:', response.status);
      console.log('Garden response headers:', Object.fromEntries(response.headers.entries()));

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        console.error('Garden API error:', errorData);
        
        if (response.status === 401) {
          console.log('Unauthorized, redirecting to login');
          localStorage.removeItem('authToken');
          navigate('/login');
          return;
        }
        
        throw new Error(errorData.message || `HTTP ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();
      console.log('Garden data received:', data);
      
      if (data.success) {
        setGardenWords(data.data.garden.words || []);
        setStats(data.data.stats);
      } else {
        throw new Error(data.message || 'Failed to fetch garden');
      }
    } catch (err) {
      console.error('Error fetching garden:', err);
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch garden';
      setError(errorMessage);
      
      // If it's a network error, provide more specific guidance
      if (errorMessage.includes('Failed to fetch') || errorMessage.includes('NetworkError')) {
        setError('Network error: Unable to connect to the server. Please check your internet connection and try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  // Removed local API toggle functionality

  const removeWordFromGarden = async (wordId: string) => {
    try {
      const token = localStorage.getItem('authToken');
      if (!token) {
        navigate('/login');
        return;
      }

      const apiUrl = env.apiUrl;
      const response = await fetch(`${apiUrl}/api/garden/words/${wordId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('Failed to remove word from garden');
      }

      const data = await response.json();
      
      if (data.success) {
        setGardenWords(prev => prev.filter(word => word.wordId !== wordId));
        // Refresh stats
        fetchGarden();
      } else {
        throw new Error(data.message || 'Failed to remove word');
      }
    } catch (err) {
      console.error('Error removing word:', err);
      setError(err instanceof Error ? err.message : 'Failed to remove word');
    }
  };

  const updateWordMastery = async (wordId: string, mastery: number) => {
    try {
      const token = localStorage.getItem('authToken');
      if (!token) {
        navigate('/login');
        return;
      }

      const apiUrl = env.apiUrl;
      const response = await fetch(`${apiUrl}/api/garden/words/${wordId}/mastery`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ mastery })
      });

      if (!response.ok) {
        throw new Error('Failed to update word mastery');
      }

      const data = await response.json();
      
      if (data.success) {
        setGardenWords(prev => 
          prev.map(word => 
            word.wordId === wordId 
              ? { ...word, mastery, lastReviewed: new Date().toISOString(), reviewCount: word.reviewCount + 1 }
              : word
          )
        );
      } else {
        throw new Error(data.message || 'Failed to update mastery');
      }
    } catch (err) {
      console.error('Error updating mastery:', err);
      setError(err instanceof Error ? err.message : 'Failed to update mastery');
    }
  };

  // Filter and sort words
  const filteredAndSortedWords = gardenWords
    .filter(word => {
      const matchesSearch = searchQuery === '' || 
        word.farsi.toLowerCase().includes(searchQuery.toLowerCase()) ||
        word.english.toLowerCase().includes(searchQuery.toLowerCase()) ||
        word.transliteration.toLowerCase().includes(searchQuery.toLowerCase());
      
      const matchesCategory = selectedCategory === 'all' || word.category === selectedCategory;
      const matchesDifficulty = selectedDifficulty === 'all' || word.difficulty === selectedDifficulty;
      
      return matchesSearch && matchesCategory && matchesDifficulty;
    })
    .sort((a, b) => {
      let comparison = 0;
      
      switch (sortBy) {
        case 'addedAt':
          comparison = new Date(a.addedAt).getTime() - new Date(b.addedAt).getTime();
          break;
        case 'mastery':
          comparison = a.mastery - b.mastery;
          break;
        case 'farsi':
          comparison = a.farsi.localeCompare(b.farsi);
          break;
        case 'english':
          comparison = a.english.localeCompare(b.english);
          break;
      }
      
      return sortOrder === 'asc' ? comparison : -comparison;
    });

  const getDifficultyIcon = (difficulty: string) => {
    switch (difficulty) {
      case 'beginner': return <FaSeedling className="text-green-500" />;
      case 'intermediate': return <FaLeaf className="text-yellow-500" />;
      case 'advanced': return <FaTree className="text-orange-500" />;
      case 'expert': return <FaTree className="text-red-500" />;
      default: return <FaSeedling className="text-green-500" />;
    }
  };

  const getMasteryStars = (mastery: number) => {
    const stars = [];
    const fullStars = Math.floor(mastery / 20);
    const hasHalfStar = mastery % 20 >= 10;
    
    for (let i = 0; i < 5; i++) {
      if (i < fullStars) {
        stars.push(<FaStar key={i} className="text-yellow-400" />);
      } else if (i === fullStars && hasHalfStar) {
        stars.push(<FaStarHalfAlt key={i} className="text-yellow-400" />);
      } else {
        stars.push(<FaStar key={i} className="text-gray-300" />);
      }
    }
    
    return stars;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-green-50 to-blue-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-500 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading your garden...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-green-50 to-blue-50 flex items-center justify-center">
        <div className="text-center max-w-md mx-auto px-4">
          <div className="text-red-500 text-6xl mb-4">🌱</div>
          <h2 className="text-2xl font-bold text-gray-800 mb-2">Oops! Something went wrong</h2>
          <p className="text-gray-600 mb-4">{error}</p>
          <div className="space-y-2">
            <button 
              onClick={fetchGarden}
              className="bg-green-500 text-white px-6 py-2 rounded-full hover:bg-green-600 transition-colors mr-2"
            >
              Try Again
            </button>
            <Link 
              to="/vocabulary-garden"
              className="inline-block bg-blue-500 text-white px-6 py-2 rounded-full hover:bg-blue-600 transition-colors"
            >
              Go to Vocabulary Garden
            </Link>
          </div>
          {process.env.NODE_ENV === 'development' && (
            <div className="mt-4 p-4 bg-gray-100 rounded-lg text-left text-sm">
              <h3 className="font-bold mb-2">Debug Information:</h3>
              <p><strong>API URL:</strong> {env.apiUrl}</p>
              <p><strong>User:</strong> {user ? 'Logged in' : 'Not logged in'}</p>
              <p><strong>Token:</strong> {localStorage.getItem('authToken') ? 'Present' : 'Missing'}</p>
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 to-blue-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b border-green-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center space-x-4">
              <Link 
                to="/vocabulary-garden" 
                className="flex items-center text-green-600 hover:text-green-700 transition-colors"
              >
                <FaArrowLeft className="mr-2" />
                Back to Garden
              </Link>
              <div className="flex items-center space-x-2">
                <div className="text-3xl">🌱</div>
                <h1 className="text-2xl font-bold text-gray-800">My Garden</h1>
              </div>
            </div>
            <div className="flex items-center space-x-4">
              <button
                onClick={() => setViewMode(viewMode === 'grid' ? 'list' : 'grid')}
                className="p-2 text-gray-600 hover:text-green-600 transition-colors"
              >
                {viewMode === 'grid' ? '📋' : '🔲'}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Stats Section */}
      {stats && (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            <div className="bg-white rounded-xl p-6 shadow-sm border border-green-200">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">Total Words</p>
                  <p className="text-3xl font-bold text-green-600">{stats.totalWords}</p>
                </div>
                <div className="text-3xl">🌿</div>
              </div>
            </div>
            
            <div className="bg-white rounded-xl p-6 shadow-sm border border-green-200">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">Average Mastery</p>
                  <p className="text-3xl font-bold text-blue-600">{Math.round(stats.averageMastery)}%</p>
                </div>
                <div className="text-3xl">⭐</div>
              </div>
            </div>
            
            <div className="bg-white rounded-xl p-6 shadow-sm border border-green-200">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">Need Review</p>
                  <p className="text-3xl font-bold text-orange-600">{stats.wordsNeedingReview}</p>
                </div>
                <div className="text-3xl">⏰</div>
              </div>
            </div>
            
            <div className="bg-white rounded-xl p-6 shadow-sm border border-green-200">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">Categories</p>
                  <p className="text-3xl font-bold text-purple-600">{stats.categories.length}</p>
                </div>
                <div className="text-3xl">🏷️</div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Filters and Search */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-8">
        <div className="bg-white rounded-xl p-6 shadow-sm border border-green-200">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {/* Search */}
            <div className="relative">
              <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder="Search words..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
              />
            </div>

            {/* Category Filter */}
            <select
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
            >
              <option value="all">All Categories</option>
              {stats?.categories.map(category => (
                <option key={category} value={category}>{category}</option>
              ))}
            </select>

            {/* Difficulty Filter */}
            <select
              value={selectedDifficulty}
              onChange={(e) => setSelectedDifficulty(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
            >
              <option value="all">All Difficulties</option>
              <option value="beginner">Beginner</option>
              <option value="intermediate">Intermediate</option>
              <option value="advanced">Advanced</option>
              <option value="expert">Expert</option>
            </select>

            {/* Sort */}
            <select
              value={`${sortBy}-${sortOrder}`}
              onChange={(e) => {
                const [sort, order] = e.target.value.split('-');
                setSortBy(sort as any);
                setSortOrder(order as any);
              }}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
            >
              <option value="addedAt-desc">Newest First</option>
              <option value="addedAt-asc">Oldest First</option>
              <option value="mastery-desc">Mastery (High to Low)</option>
              <option value="mastery-asc">Mastery (Low to High)</option>
              <option value="farsi-asc">Farsi (A-Z)</option>
              <option value="english-asc">English (A-Z)</option>
            </select>
          </div>
        </div>
      </div>

      {/* Words Grid/List */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-8">
        {filteredAndSortedWords.length === 0 ? (
          <div className="text-center py-12">
            <div className="text-6xl mb-4">🌱</div>
            <h3 className="text-xl font-semibold text-gray-800 mb-2">Your garden is empty</h3>
            <p className="text-gray-600 mb-6">Start adding words to your garden to see them here!</p>
            <Link 
              to="/vocabulary-garden"
              className="inline-flex items-center px-6 py-3 bg-green-500 text-white rounded-full hover:bg-green-600 transition-colors"
            >
              <FaPlus className="mr-2" />
              Explore Vocabulary Garden
            </Link>
          </div>
        ) : (
          <div className={viewMode === 'grid' 
            ? 'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6'
            : 'space-y-4'
          }>
            {filteredAndSortedWords.map((word) => (
              <div 
                key={word.wordId} 
                className={`bg-white rounded-xl shadow-sm border border-green-200 hover:shadow-md transition-shadow ${
                  viewMode === 'list' ? 'flex items-center p-6' : 'p-6'
                }`}
              >
                {viewMode === 'grid' ? (
                  // Grid View
                  <div>
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex items-center space-x-2">
                        {getDifficultyIcon(word.difficulty || 'beginner')}
                        <span className="text-sm text-gray-500 capitalize">{word.difficulty || 'beginner'}</span>
                      </div>
                      <button
                        onClick={() => removeWordFromGarden(word.wordId)}
                        className="text-red-400 hover:text-red-600 transition-colors"
                      >
                        <FaTrash />
                      </button>
                    </div>

                    <div className="text-center mb-4">
                      <h3 className="text-2xl font-bold text-gray-800 mb-1">{word.farsi}</h3>
                      <p className="text-sm text-gray-500 mb-1">{word.transliteration}</p>
                      <p className="text-lg font-semibold text-gray-700">{word.english}</p>
                    </div>

                    <div className="flex items-center justify-center mb-4">
                      {getMasteryStars(word.mastery)}
                    </div>

                    <div className="flex items-center justify-between">
                      <button
                        onClick={() => playSmartAudio(undefined, word.farsi)}
                        className="p-2 text-green-600 hover:text-green-700 hover:bg-green-50 rounded-full transition-colors"
                      >
                        <FaVolumeUp />
                      </button>
                      
                      <div className="flex space-x-2">
                        <button
                          onClick={() => updateWordMastery(word.wordId, Math.max(0, word.mastery - 20))}
                          className="px-3 py-1 text-sm bg-gray-100 text-gray-600 rounded-full hover:bg-gray-200 transition-colors"
                        >
                          -
                        </button>
                        <span className="px-3 py-1 text-sm bg-green-100 text-green-700 rounded-full">
                          {word.mastery}%
                        </span>
                        <button
                          onClick={() => updateWordMastery(word.wordId, Math.min(100, word.mastery + 20))}
                          className="px-3 py-1 text-sm bg-gray-100 text-gray-600 rounded-full hover:bg-gray-200 transition-colors"
                        >
                          +
                        </button>
                      </div>
                    </div>

                    {word.category && (
                      <div className="mt-3">
                        <span className="inline-block px-2 py-1 text-xs bg-blue-100 text-blue-700 rounded-full">
                          {word.category}
                        </span>
                      </div>
                    )}
                  </div>
                ) : (
                  // List View
                  <>
                    <div className="flex-1">
                      <div className="flex items-center space-x-4">
                        <div className="flex items-center space-x-2">
                          {getDifficultyIcon(word.difficulty || 'beginner')}
                          <span className="text-sm text-gray-500 capitalize">{word.difficulty || 'beginner'}</span>
                        </div>
                        <div>
                          <h3 className="text-xl font-bold text-gray-800">{word.farsi}</h3>
                          <p className="text-sm text-gray-500">{word.transliteration}</p>
                          <p className="text-lg font-semibold text-gray-700">{word.english}</p>
                        </div>
                      </div>
                    </div>

                    <div className="flex items-center space-x-4">
                      <div className="flex items-center space-x-1">
                        {getMasteryStars(word.mastery)}
                      </div>
                      
                      <div className="flex items-center space-x-2">
                        <button
                          onClick={() => updateWordMastery(word.wordId, Math.max(0, word.mastery - 20))}
                          className="px-2 py-1 text-sm bg-gray-100 text-gray-600 rounded hover:bg-gray-200 transition-colors"
                        >
                          -
                        </button>
                        <span className="px-3 py-1 text-sm bg-green-100 text-green-700 rounded">
                          {word.mastery}%
                        </span>
                        <button
                          onClick={() => updateWordMastery(word.wordId, Math.min(100, word.mastery + 20))}
                          className="px-2 py-1 text-sm bg-gray-100 text-gray-600 rounded hover:bg-gray-200 transition-colors"
                        >
                          +
                        </button>
                      </div>

                      <button
                        onClick={() => playSmartAudio(undefined, word.farsi)}
                        className="p-2 text-green-600 hover:text-green-700 hover:bg-green-50 rounded-full transition-colors"
                      >
                        <FaVolumeUp />
                      </button>

                      <button
                        onClick={() => removeWordFromGarden(word.wordId)}
                        className="p-2 text-red-400 hover:text-red-600 transition-colors"
                      >
                        <FaTrash />
                      </button>
                    </div>
                  </>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default MyGarden;
